// MobileLeadsA — Variation A · Vibrant mobile Leads list.
// Rendered by pages/Leads.js when viewport <768px AND user is admin/super_admin.
// Self-contained: ships with its own minimal Add Lead bottom sheet so the FAB
// works without coupling to the desktop dialog plumbing.
//
// Design source: public/design/dnexus-mobile-crm.html  (lines 177-294)

import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import MobileBottomTabBar from "./MobileBottomTabBar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FONT = "'Plus Jakarta Sans', system-ui, sans-serif";

// ─── helpers ────────────────────────────────────────────────────────────────

function initialsOf(name) {
  return String(name || "??")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase() || "??";
}

const GRADIENTS = [
  "linear-gradient(135deg,#818CF8,#6366F1)",
  "linear-gradient(135deg,#FBBF24,#F59E0B)",
  "linear-gradient(135deg,#38BDF8,#0EA5E9)",
  "linear-gradient(135deg,#34D399,#10B981)",
  "linear-gradient(135deg,#FB7185,#F43F5E)",
  "linear-gradient(135deg,#A78BFA,#8B5CF6)",
];
function gradientFor(name) {
  const s = String(name || "");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return GRADIENTS[h % GRADIENTS.length];
}

function relativeTime(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (!t) return "";
  const diff = Math.floor((Date.now() - t) / 1000);
  if (diff < 60) return "now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return `${Math.floor(diff / 604800)}w`;
}

function formatINRShort(n) {
  const v = Number(n);
  if (!v) return "—";
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)}Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(1)}L`;
  if (v >= 1e3) return `₹${Math.round(v / 1e3)}K`;
  return `₹${v.toLocaleString("en-IN")}`;
}

// Map a stage/status string to a colored pill style. Tolerates the many
// variants ("New", "new lead", "Won / Converted", etc.) the CRM has.
function stagePillStyle(stage) {
  const s = String(stage || "").toLowerCase().trim();
  if (!s || s === "new" || s === "new lead") return { bg: "#EEF0FF", fg: "#5B50E6", label: "New" };
  if (s.includes("contact"))   return { bg: "#FEF4E6", fg: "#B45309", label: "Contacted" };
  if (s.includes("qualif"))    return { bg: "#E6F6FD", fg: "#0369A1", label: "Qualified" };
  if (s.includes("proposal"))  return { bg: "#F3EDFE", fg: "#7C3AED", label: "Proposal" };
  if (s.includes("negoti"))    return { bg: "#FEF4E6", fg: "#B45309", label: "Negotiation" };
  if (s.includes("won") || s.includes("convert") || s.includes("client")) return { bg: "#E7F8F0", fg: "#047857", label: "Converted" };
  if (s.includes("paid") || s.includes("payment")) return { bg: "#E7F8F0", fg: "#047857", label: "Paid" };
  if (s.includes("lost"))      return { bg: "#FFF1F2", fg: "#E11D48", label: "Lost" };
  if (s.includes("hold"))      return { bg: "#F2F3F6", fg: "#475569", label: "On Hold" };
  if (s.includes("follow"))    return { bg: "#FFF7ED", fg: "#C2410C", label: "Follow-Up" };
  // Default — use the original label, neutral colors
  return { bg: "#F2F3F6", fg: "#475569", label: stage || "—" };
}

// Filter chips definition. `match(lead)` returns true if the lead belongs.
const FILTER_CHIPS = [
  { key: "all",       label: "All",        accent: { bg: "#6366F1", fg: "#fff" } },
  { key: "new",       label: "New",        match: (l) => /^new/i.test(l.stage || "") || !l.stage, accent: { bg: "#EEF0FF", fg: "#5B50E6" } },
  { key: "contacted", label: "Contacted",  match: (l) => /contact/i.test(l.stage || ""), accent: { bg: "#FEF4E6", fg: "#B45309" } },
  { key: "qualified", label: "Qualified",  match: (l) => /qualif/i.test(l.stage || ""), accent: { bg: "#E6F6FD", fg: "#0369A1" } },
  { key: "won",       label: "Converted",  match: (l) => /(won|convert|client)/i.test(l.stage || ""), accent: { bg: "#E7F8F0", fg: "#047857" } },
  { key: "lost",      label: "Lost",       match: (l) => /lost/i.test(l.stage || ""), accent: { bg: "#FFF1F2", fg: "#E11D48" } },
];

// ─── Add Lead Sheet (minimal, mobile-native) ────────────────────────────────

function AddLeadSheet({ open, onClose, businessId, businessUsers = [], sources = [], onCreated }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [source, setSource] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(""); setPhone(""); setSource(""); setAssignedTo(""); setSaving(false);
    }
  }, [open]);

  if (!open) return null;

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    if (!phone.trim()) { toast.error("Phone is required"); return; }
    setSaving(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: name.trim(),
          phone: phone.trim(),
          source: source || "",
          assigned_to: assignedTo || "",
          business_id: businessId,
          stage: "New Lead",
          status: "new",
        }),
      });
      if (r.ok) {
        const lead = await r.json().catch(() => ({}));
        toast.success("Lead added");
        onClose();
        if (onCreated) onCreated(lead);
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || "Failed to create lead");
      }
    } catch (e) {
      toast.error("Failed to create lead");
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end"
      style={{ background: "rgba(15,15,25,.45)" }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full bg-white rounded-t-3xl"
        style={{
          fontFamily: FONT,
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 18px)",
          boxShadow: "0 -16px 32px rgba(15,15,40,.18)",
          maxHeight: "85vh",
          overflowY: "auto",
        }}
      >
        <div style={{ width: 44, height: 5, borderRadius: 99, background: "#E5E7EB", margin: "10px auto 4px" }} />
        <div style={{ padding: "10px 20px 18px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: "#1B1F2A", letterSpacing: "-.3px" }}>Add Lead</div>
            <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: "50%", background: "#F2F3F6", display: "flex", alignItems: "center", justifyContent: "center", border: "none" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="#5B5F6B" strokeWidth="2" strokeLinecap="round"/></svg>
            </button>
          </div>

          <Field label="Name *">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" autoFocus className="mleadsa-input" />
          </Field>
          <Field label="Phone *">
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 98xxxxxxxx" type="tel" className="mleadsa-input" />
          </Field>
          <Field label="Source">
            <select value={source} onChange={(e) => setSource(e.target.value)} className="mleadsa-input">
              <option value="">— Select —</option>
              {sources.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Assign To">
            <select value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} className="mleadsa-input">
              <option value="">Unassigned</option>
              {businessUsers.map((u) => <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>)}
            </select>
          </Field>

          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="mobile-add-lead-save"
            style={{
              width: "100%",
              marginTop: 18,
              padding: "14px",
              borderRadius: 16,
              background: saving ? "#A5B4FC" : "linear-gradient(135deg,#818CF8,#6366F1)",
              color: "#fff",
              fontSize: 15,
              fontWeight: 800,
              border: "none",
              boxShadow: "0 12px 22px rgba(99,102,241,.32)",
            }}
          >
            {saving ? "Saving…" : "Add Lead"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Helper to render label + input with consistent style (works around inline-style sprawl)
function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: ".3px", marginBottom: 6 }}>{label}</label>
      {children}
    </div>
  );
}
// Apply consistent input styling via a global class injected once at module load
if (typeof document !== "undefined" && !document.getElementById("__mobile_leads_a_input_css__")) {
  const s = document.createElement("style");
  s.id = "__mobile_leads_a_input_css__";
  s.textContent = `
    .mleadsa-input {
      width: 100%; padding: 11px 14px; border: 1px solid #ECECF3; border-radius: 12px;
      background: #F9F9FC; color: #1B1F2A; font-size: 14px; font-weight: 500;
      outline: none; font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    }
    .mleadsa-input:focus { border-color: #6366F1; background: #fff; box-shadow: 0 0 0 3px rgba(99,102,241,.12); }
  `;
  document.head.appendChild(s);
}

// ─── Main component ─────────────────────────────────────────────────────────

export default function MobileLeadsA({
  leads = [],
  loading = false,
  onLeadClick,
  businessId,
  businessUsers = [],
  sources = [],
  onLeadCreated,
  totalLeadsCount,
  lostLeadsCount,
}) {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [addOpen, setAddOpen] = useState(false);

  // Computed counts for filter chips
  const counts = useMemo(() => {
    const out = { all: leads.length };
    for (const c of FILTER_CHIPS) {
      if (c.key === "all") continue;
      out[c.key] = leads.filter(c.match).length;
    }
    return out;
  }, [leads]);

  const visibleLeads = useMemo(() => {
    let list = leads;
    if (filter !== "all") {
      const def = FILTER_CHIPS.find((c) => c.key === filter);
      if (def?.match) list = list.filter(def.match);
    }
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((l) =>
        (l.name || "").toLowerCase().includes(q) ||
        (l.phone || "").toLowerCase().includes(q) ||
        (l.email || "").toLowerCase().includes(q) ||
        (l.company || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [leads, filter, search]);

  const totalCount = totalLeadsCount != null ? totalLeadsCount : leads.length;
  const lostCount = lostLeadsCount != null ? lostLeadsCount : counts.lost;

  return (
    <div
      className="bg-[#F6F6FB] flex flex-col"
      style={{
        margin: "-1rem -1rem 0 -1rem",
        minHeight: "calc(100vh - 4rem)",
        fontFamily: FONT,
        paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 84px)",
      }}
      data-testid="mobile-leads-a"
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px 6px" }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 800, color: "#1B1F2A", letterSpacing: "-.5px" }}>Leads</div>
          <div style={{ fontSize: 12, color: "#8A8F9C", fontWeight: 600 }}>
            <b style={{ color: "#6366F1" }}>{totalCount.toLocaleString("en-IN")}</b> total
            {lostCount > 0 && <> · {lostCount} lost</>}
          </div>
        </div>
        <button
          onClick={() => setAddOpen(true)}
          style={{
            width: 40, height: 40, borderRadius: 13,
            background: "linear-gradient(135deg,#818CF8,#6366F1)",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 8px 16px rgba(99,102,241,.32)",
            border: "none",
          }}
          aria-label="Add lead"
          data-testid="mobile-leads-add-btn"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"/></svg>
        </button>
      </div>

      {/* Search */}
      <div style={{ padding: "6px 16px 8px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#fff", border: "1px solid #ECECF3", borderRadius: 14, padding: "10px 13px" }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="7" stroke="#9AA0AE" strokeWidth="1.8"/><path d="M20 20l-4-4" stroke="#9AA0AE" strokeWidth="1.8" strokeLinecap="round"/></svg>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search leads…"
            style={{ flex: 1, border: "none", outline: "none", background: "transparent", fontSize: 13, color: "#1B1F2A", fontFamily: FONT }}
            data-testid="mobile-leads-search"
          />
          {search && (
            <button onClick={() => setSearch("")} style={{ background: "none", border: "none", padding: 0, lineHeight: 0 }} aria-label="Clear search">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="#E5E7EB"/><path d="M8 8l8 8M16 8l-8 8" stroke="#6B7280" strokeWidth="2" strokeLinecap="round"/></svg>
            </button>
          )}
        </div>
      </div>

      {/* Filter chips */}
      <div
        style={{ display: "flex", gap: 8, overflowX: "auto", padding: "2px 16px 10px", scrollbarWidth: "none" }}
        className="[&::-webkit-scrollbar]:hidden"
      >
        {FILTER_CHIPS.map((c) => {
          const isActive = filter === c.key;
          const count = c.key === "all" ? totalCount : (counts[c.key] || 0);
          // Lost chip gets red pill when not active (per design)
          const isLostIdle = c.key === "lost" && !isActive && count > 0;
          return (
            <button
              key={c.key}
              onClick={() => setFilter(c.key)}
              style={{
                flex: "0 0 auto",
                background: isActive ? c.accent.bg : (isLostIdle ? "#FFF1F2" : "#fff"),
                color: isActive ? c.accent.fg : (isLostIdle ? "#E11D48" : "#5B5F6B"),
                border: isActive ? "none" : (isLostIdle ? "1px solid #FFE0E3" : "1px solid #ECECF3"),
                fontSize: 12, fontWeight: 700, padding: "7px 13px", borderRadius: 999,
                whiteSpace: "nowrap",
              }}
              data-testid={`mobile-leads-filter-${c.key}`}
            >
              {c.label}
              {count > 0 && <span style={{ marginLeft: 5, opacity: isActive ? .95 : .8 }}>{count}</span>}
            </button>
          );
        })}
      </div>

      {/* List */}
      <div style={{ flex: 1, padding: "4px 16px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
        {loading && leads.length === 0 ? (
          // 3 skeleton cards
          [0, 1, 2].map((i) => (
            <div key={i} style={{ background: "#fff", border: "1px solid #EFEFF5", borderRadius: 18, padding: 13, height: 90, opacity: .6 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
                <div style={{ width: 42, height: 42, borderRadius: 13, background: "#E5E7EB" }} />
                <div style={{ flex: 1 }}>
                  <div style={{ height: 12, background: "#E5E7EB", borderRadius: 4, width: "60%", marginBottom: 8 }} />
                  <div style={{ height: 10, background: "#E5E7EB", borderRadius: 4, width: "40%" }} />
                </div>
              </div>
            </div>
          ))
        ) : visibleLeads.length === 0 ? (
          <div style={{ background: "#fff", border: "1px solid #EFEFF5", borderRadius: 18, padding: 40, textAlign: "center" }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#1B1F2A", marginBottom: 4 }}>
              {leads.length === 0 ? "No leads yet" : "No matches"}
            </div>
            <div style={{ fontSize: 12, color: "#8A8F9C" }}>
              {leads.length === 0 ? "Tap + to add your first lead." : "Try a different search or filter."}
            </div>
          </div>
        ) : (
          visibleLeads.map((lead) => {
            const stage = stagePillStyle(lead.stage || lead.status);
            const displayName = lead.name || lead.patient_name || lead.student_name || lead.company_name || lead.parent_name || lead.phone || "Unnamed";
            const sub2 =
              lead.assigned_to_name ? `Assigned · ${lead.assigned_to_name}` :
              lead.phone ? lead.phone :
              lead.email ? lead.email : "—";
            const isLost = /lost/i.test(lead.stage || "");
            return (
              <button
                key={lead.lead_id || lead._id}
                onClick={() => onLeadClick && onLeadClick(lead.lead_id || lead._id)}
                style={{
                  background: "#fff", border: "1px solid #EFEFF5", borderRadius: 18, padding: 13,
                  boxShadow: "0 1px 3px rgba(17,17,40,.04)",
                  opacity: isLost ? .85 : 1,
                  textAlign: "left", width: "100%", cursor: "pointer",
                  fontFamily: FONT,
                }}
                data-testid={`mobile-lead-card-${lead.lead_id || lead._id}`}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
                  <div
                    style={{
                      width: 42, height: 42, borderRadius: 13,
                      background: gradientFor(displayName),
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: "#fff", fontWeight: 800, fontSize: 15, flexShrink: 0,
                    }}
                  >
                    {initialsOf(displayName)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                      <span style={{ fontSize: 14.5, fontWeight: 800, color: "#1B1F2A", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{displayName}</span>
                      <span style={{ fontSize: 11, color: "#9AA0AE", fontWeight: 600, flexShrink: 0 }}>{relativeTime(lead.created_at)}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 4, overflow: "hidden" }}>
                      <span style={{ fontSize: 11, color: "#6B7280", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{lead.source || "Manual"}</span>
                      <span style={{ width: 3, height: 3, borderRadius: "50%", background: "#CBD0DA", flexShrink: 0 }} />
                      <span style={{ fontSize: 11, color: "#6B7280", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sub2}</span>
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 11 }}>
                  <span style={{ fontSize: 11, fontWeight: 800, color: stage.fg, background: stage.bg, padding: "4px 10px", borderRadius: 999 }}>
                    ● {stage.label}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 800, color: lead.deal_value ? "#1B1F2A" : "#9AA0AE" }}>
                    {formatINRShort(lead.deal_value)}
                  </span>
                </div>
              </button>
            );
          })
        )}
      </div>

      {/* Add Lead sheet */}
      <AddLeadSheet
        open={addOpen}
        onClose={() => setAddOpen(false)}
        businessId={businessId}
        businessUsers={businessUsers}
        sources={sources}
        onCreated={onLeadCreated}
      />

      {/* Bottom tab bar */}
      <MobileBottomTabBar active="leads" />
    </div>
  );
}
